import { TFunction } from 'i18next';
import _get from 'lodash/get';
import _camelCase from 'lodash/camelCase';
import { DataImportKindEnum } from 'graphql-common';
import getFieldName from './getFieldName';

export type ErrorFieldValue =
  | string[]
  | {
      [index: string]:
        | string[]
        | {
            [subField: string]: string[];
          };
    };

export interface ErrorData {
  [rowIndex: string]: {
    [fieldName: string]: ErrorFieldValue;
  };
}

export interface ProcessedError {
  fieldName: string;
  cell: string;
  errors: string[];
}

function addOrUpdateError({
  processedErrors,
  fieldName,
  cell,
  errorMessages,
  errorCode,
}: {
  processedErrors: ProcessedError[];
  fieldName: string;
  cell: string;
  errorMessages: string[];
  errorCode: string;
}): number {
  let newErrorsCount = 0;
  const existingError = processedErrors.find((e) => e.fieldName === fieldName);
  if (existingError) {
    errorMessages.forEach((msg) => {
      const existingMessage = existingError.errors.find(
        (eMsg) => eMsg.split(': ')[1] === msg,
      );
      if (existingMessage) {
        const codes = existingMessage.split(': ')[0].split(', ');
        if (!codes.includes(errorCode)) {
          codes.push(errorCode);
          existingError.errors[existingError.errors.indexOf(existingMessage)] =
            `${codes.join(', ')}: ${msg}`;
        }
      } else {
        existingError.errors.push(`${errorCode}: ${msg}`);
      }
      newErrorsCount += 1;
    });
  } else {
    processedErrors.push({
      cell,
      errors: errorMessages.map((msg) => `${errorCode}: ${msg}`),
      fieldName,
    });
    newErrorsCount += errorMessages.length;
  }
  return newErrorsCount;
}

function processDeepNestedErrors({
  processedErrors,
  obj,
  errorCode,
  path = '',
  config,
  kind,
  t,
}: {
  processedErrors: ProcessedError[];
  obj: any;
  errorCode: string;
  path?: string;
  config: object;
  kind: DataImportKindEnum;
  t: TFunction<'translation', undefined>;
}): number {
  let errorCount = 0;

  if (Array.isArray(obj)) {
    // Found error messages array
    const cell = _get(config, path.split('.'), '');
    const fieldName = getFieldName({
      field: path,
      cell,
      t,
    });
    if (fieldName) {
      errorCount += addOrUpdateError({
        processedErrors,
        fieldName,
        cell,
        errorMessages: obj as string[],
        errorCode,
      });
    }
  } else if (typeof obj === 'object' && obj !== null) {
    // Recursively process nested objects
    Object.entries(obj).forEach(([key, value]) => {
      const newPath = path ? `${path}.${key}` : key;
      errorCount += processDeepNestedErrors({
        processedErrors,
        obj: value,
        errorCode,
        path: newPath,
        config,
        kind,
        t,
      });
    });
  }

  return errorCount;
}

export default function processErrors({
  config,
  failureDetails,
  t,
  kind,
}: {
  config: object;
  failureDetails: ErrorData;
  t: TFunction<'translation', undefined>;
  kind: DataImportKindEnum;
}): { processedErrors: ProcessedError[]; errorsTotalCount: number } {
  const processedErrors: ProcessedError[] = [];
  let errorsTotalCount = 0;

  if (!failureDetails) return { processedErrors, errorsTotalCount };

  Object.entries(failureDetails).forEach(([errorCode, errors]) => {
    Object.entries(errors).forEach(([field, value]) => {
      if (Array.isArray(value)) {
        // Handle a first level errors case
        const cell = _get(config, [_camelCase(kind), field], '');
        const fieldName = getFieldName({
          field: `${kind.toLowerCase()}.${field}`,
          cell,
          t,
        });
        if (fieldName) {
          errorsTotalCount += addOrUpdateError({
            processedErrors,
            fieldName,
            cell,
            errorMessages: value as string[],
            errorCode,
          });
        }
      } else if (typeof value === 'object' && value !== null) {
        // Handle a nested errors case
        Object.entries(value).forEach(([subField, subValue]) => {
          // Special handling for 'areas'
          if (field === 'areas' && typeof subValue === 'object' && subValue) {
            Object.entries(subValue as Record<string, string[]>).forEach(
              ([subSubField, arr]) => {
                if (Array.isArray(arr)) {
                  const cell = _get(config, ['siteArea', subSubField], '');
                  const fieldName = getFieldName({
                    field: `${kind.toLowerCase()}.${subSubField}`,
                    cell,
                    t,
                  });
                  if (fieldName) {
                    errorsTotalCount += addOrUpdateError({
                      processedErrors,
                      fieldName,
                      cell,
                      errorMessages: arr,
                      errorCode,
                    });
                  }
                }
              },
            );
          } else {
            // General nested field error handling
            const arr = subValue as string[];
            if (Array.isArray(arr)) {
              const cell = _get(
                config,
                [_camelCase(kind), field, subField],
                '',
              );
              const fieldName = getFieldName({
                field: `${kind.toLowerCase()}.${field}.${subField}`,
                cell,
                t,
              });
              if (fieldName) {
                errorsTotalCount += addOrUpdateError({
                  processedErrors,
                  fieldName,
                  cell,
                  errorMessages: arr,
                  errorCode,
                });
              }
            } else {
              // Handle deeply nested structures (like geojson coordinates)
              errorsTotalCount += processDeepNestedErrors({
                processedErrors,
                obj: subValue,
                errorCode,
                path: `${kind.toLowerCase()}.${field}.${subField}`,
                config,
                kind,
                t,
              });
            }
          }
        });
      }
    });
  });

  return {
    processedErrors: processedErrors.sort((a, b) =>
      a.cell.localeCompare(b.cell, undefined, { sensitivity: 'base' }),
    ),
    errorsTotalCount,
  };
}
